import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataView } from 'primereact/dataview';
import { Divider } from 'primereact/divider';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import CartItem from '../../models/CartItem';
import { EmailService } from '../../services/email.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { Modal } from '../Modal/Modal';
import { AuthService } from '../../services/auth.service';
import './SideBar.css';
import { DelegateService } from '../../services/delegate.service';
import { KamService } from '../../services/kam.service';
import ExceededItem from '../../models/ExceededItem';
import PurchaseOrder from '../../models/PurchaseOrder';
import EmailRequest from '../../models/EmailRequest';
import { formatCurrency } from '../../utils/formatCurrency';

interface SideBarProps {
  visibleSidebar: boolean;
  setVisibleSideBar: (visible: boolean) => void;
}

export default function SideBar({
  visibleSidebar,
  setVisibleSideBar,
}: SideBarProps) {
  const { t } = useTranslation();
  const [visibleModal, setVisibleModal] = useState(false);
  const {
    cartItems,
    increaseCartQuantity,
    decreaseCartQuantity,
    setQuantity,
    removeFromCart,
    emptyCart,
  } = useShoppingCart();

  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [editingValues, setEditingValues] = useState<{ [key: string]: string }>(
    {},
  );

  const exceededProducts = cartItems.filter((item) => item.quantity > 2000);
  const cartTotal = cartItems.reduce(
    (accumulator, currentItem) =>
      accumulator + currentItem.price * currentItem.quantity,
    0,
  );

  const accept = async (journalId: string) => {
    try {
      const role = AuthService.GetRole();

      if (
        role === 'Delegate' ||
        role === 'KAM' ||
        role === 'Congress' ||
        role === 'Central'
      ) {
        let idCompany = '';
        let idClient = '';

        if (role === 'Delegate') {
          idCompany = DelegateService.GetDelegateCompanyID() || '';
          idClient = DelegateService.GetDelegateCustomerID() || '';
        } else if (role === 'Central') {
          idCompany = 'meso';
          idClient = '038702';
        } else if (role === 'Congress') {
          idCompany = DelegateService.GetDelegateCompanyID() || '';
          idClient = DelegateService.GetDelegateCustomerID() || '';
        } else {
          idCompany = KamService.GetKamCompanyID() || '';
          idClient = KamService.GetKamCustomerID() || '';
        }

        // Validación previa
        if (!idCompany || !idClient) {
          // Mostrar modal o toast indicando que falta info
          toast.current?.show({
            severity: 'warn',
            summary: 'Datos incompletos',
            detail:
              'Debe seleccionar un cliente antes de continuar. Será redirigido automáticamente en 3 segundos.',
            life: 4000,
          });

          // Redirigir a choiceCustomer después de un pequeño delay para que alcance a ver el mensaje
          setTimeout(() => {
            navigate('/choiceCustomer');
          }, 3000);

          return; // Salimos sin ejecutar la compra
        }
      }
      // Solo si pasó la validación continuamos
      await purchaseItems(journalId);

      // Después de compra, según rol mostrar modal o navegar
      if (role === 'Delegate' || role === 'KAM' || role === 'Congress') {
        navigate('/choiceCustomer');
      }
    } catch (error) {
      console.error('Error occurred while processing:', error);
      toast.current?.show({
        severity: 'error',
        summary: t('sidebar_component.toast_error_purchase.summary'),
        detail: t('sidebar_component.toast_error_purchase.detail'),
        life: 3000,
      });
    }
  };

  async function purchaseItems(journalId: string | undefined) {
    //TODO - Paula - Some fields here can be erased and changed.
    const address = AuthService.GetUserEmail() || '';
    let idCompany = '';
    let idClient = '';
    const role = AuthService.GetRole();
    if (role === 'Distributor') {
      idCompany = AuthService.GetCompanyID() || '';
      idClient = AuthService.GetCustomerID() || '';
    } else if (role === 'Delegate') {
      idCompany = DelegateService.GetDelegateCompanyID() || '';
      idClient = DelegateService.GetDelegateCustomerID() || '';
    } else if (role === 'Central') {
      idCompany = 'meso';
      idClient = '038702';
    } else if (role === 'Congress') {
      idCompany = DelegateService.GetDelegateCompanyID() || '';
      idClient = DelegateService.GetDelegateCustomerID() || '';
    } else {
      idCompany = KamService.GetKamCompanyID() || '';
      idClient = KamService.GetKamCustomerID() || '';
    }
    const newPurchaseItemsAX = cartItems.map((cartItem) => {
      return {
        idPurchaseOrder: `P2B_${Date.now()}`,
        idCompany: idCompany,
        idClient: idClient,
        partition: 5637144576,
        recVersion: 1,
        quantity: cartItem.quantity,
        price: cartItem.price,
        code: cartItem.idProduct.toString(),
        address: address,
        journalId: journalId,
      };
    });
    try {
      setVisibleModal(false);
      await PurchaseOrderService.PostPurchaseOrderAX(newPurchaseItemsAX);
      emptyCart();
      toast.current?.show({
        severity: 'info',
        summary: t(
          'sidebar_component.toast_confirmation_purchase.success.summary',
        ),
        detail: t(
          'sidebar_component.toast_confirmation_purchase.success.detail',
        ),
        life: 3000,
      });
      await sendEmail(newPurchaseItemsAX);
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        const exceededItems = error.response.data.items as ExceededItem[];

        if (exceededItems && exceededItems.length > 0) {
          const itemsList = exceededItems
            .map((item) => `• ${item.code} - ${item.name.toLocaleLowerCase()}`)
            .join('\n');

          const message = `${t('sidebar_component.toast_confirmation_purchase.warn.detail')}\n${itemsList}\n${t('sidebar_component.toast_confirmation_purchase.warn.footer')}`;

          toast.current?.show({
            severity: 'warn',
            summary: t(
              'sidebar_component.toast_confirmation_purchase.warn.summary',
            ),
            detail: message,
            life: 5000,
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t(
              'sidebar_component.toast_confirmation_purchase.error.summary',
            ),
            detail: t(
              'sidebar_component.toast_confirmation_purchase.error.summary',
            ),
            life: 5000,
          });
        }
      } else {
        console.error('Error occurred while posting purchase orders:', error);
        toast.current?.show({
          severity: 'error',
          summary: t(
            'sidebar_component.toast_confirmation_purchase.error.summary',
          ),
          detail: t(
            'sidebar_component.toast_confirmation_purchase.error.summary',
          ),
          life: 5000,
        });
      }
    }
  }

  async function sendEmail(purchasesItemsAX: PurchaseOrder[]) {
    try {
      const idLanguageEmail = AuthService.GetLanguageID() || '';
      const userMail = AuthService.GetUserEmail() || '';
      const emailRequest: EmailRequest = {
        toEmail: userMail,
        idLanguage: idLanguageEmail,
        purchaseOrders: purchasesItemsAX,
      };
      console.log('Sending email with request:', emailRequest);
      await EmailService.Send(emailRequest);
    } catch (error) {
      console.error('Error occurred while sending email:', error);
      throw new Error('Email sending failed');
    }
  }

  const commitAllEditingProducts = () => {
    cartItems.forEach((product) => {
      if (editingValues[product.idProduct] !== undefined) {
        commitQuantityChange(product);
      }
    });
  };

  const commitQuantityChange = (product: CartItem) => {
    const localValue =
      editingValues[product.idProduct] ?? product.quantity.toString();

    const finalValue = parseInt(localValue) || 0;
    setQuantity(product.idProduct, finalValue);

    setEditingValues((prev) => {
      const newPrev = { ...prev };
      delete newPrev[product.idProduct];
      return newPrev;
    });
  };

  const itemTemplate = (cartItems: CartItem[]) => {
    return cartItems.map((product, index) => {
      // Valor temporal local: usa el editingValue si existe, sino el quantity real
      const localValue =
        editingValues[product.idProduct] ?? product.quantity.toString();

      return (
        <div key={product.idProduct}>
          <div
            className={classNames(
              'flex flex-column xl:flex-row xl:align-items-start p-4 gap-4',
              { 'border-top-1 surface-border': index !== 0 },
            )}
          >
            <img
              className="w-9 sm:w-10rem xl:w-8rem shadow-2 block xl:block mx-auto border-round"
              src={product.picture}
              alt={product.displayName}
              style={{
                maxWidth: '40px',
                maxHeight: '70px',
                width: 'auto',
                height: 'auto',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder.jpg';
              }}
            />
            <div className="flex flex-column sm:flex-row justify-content-between align-items-center xl:align-items-start flex-1 gap-4">
              <div className="flex flex-column align-items-center sm:align-items-start gap-2">
                <div>
                  <strong>{product.idProduct}</strong>
                </div>
                <div
                  className="text-s font-normal text-900"
                  style={{ fontFamily: 'Graphik Light', fontSize: 'small' }}
                >
                  {product.displayName.toLocaleLowerCase()}
                </div>
                <div className="counter-div">
                  <p
                    className="icon-substract-add"
                    onClick={() => decreaseCartQuantity(product.idProduct)}
                  >
                    -
                  </p>
                  <input
                    type="number"
                    value={localValue}
                    className="sidebar-input"
                    disabled={false}
                    onKeyDown={(e) => {
                      if (
                        e.key === '.' ||
                        e.key === ',' ||
                        e.key === 'e' ||
                        e.key === 'E'
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setEditingValues((prev) => ({
                        ...prev,
                        [product.idProduct]: newValue,
                      }));
                    }}
                    onFocus={(e) => e.target.select()} // Sombrea todo al enfocar
                    onBlur={() => commitQuantityChange(product)}
                  />
                  <p
                    className="icon-substract-add"
                    onClick={() =>
                      increaseCartQuantity(
                        product.idProduct,
                        product.displayName,
                        product.price,
                        product.picture,
                        product.lowest,
                        product.multiple,
                      )
                    }
                  >
                    +
                  </p>
                  <i
                    className="pi pi-trash trash-icon"
                    onClick={() => removeFromCart(product.idProduct)}
                  ></i>
                </div>
              </div>
              <div className="flex sm:flex-column align-items-center sm:align-items-end gap-3 sm:gap-2">
                <span
                  className="text-l font-normal"
                  style={{ fontFamily: 'Graphik Light', fontSize: 'small' }}
                >
                  {formatCurrency(product.price)}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="card flex justify-content-center">
      <Sidebar
        visible={visibleSidebar}
        onHide={() => {
          commitAllEditingProducts();
          setVisibleSideBar(false);
        }}
        position="right"
        style={{ position: 'relative', zIndex: 9999 }}
      >
        <h2 className="sidebar-title">{t('sidebar_component.title')}</h2>
        {exceededProducts.length > 0 && (
          <div className="exceeded-warning">
            <strong>
              {t('sidebar_component.toast_confirmation_purchase.warn.summary')}
            </strong>{' '}
            <p>
              {t('sidebar_component.toast_confirmation_purchase.warn.detail')}
            </p>{' '}
            <ul>
              {exceededProducts.map((product) => (
                <li key={product.idProduct}>
                  {product.displayName} (
                  {t('sidebar_component.toast_confirmation_purchase.warn.max')})
                </li>
              ))}
            </ul>
            <p>
              {t('sidebar_component.toast_confirmation_purchase.warn.footer')}
            </p>{' '}
          </div>
        )}
        <DataView value={cartItems} listTemplate={itemTemplate}></DataView>
        <Toast ref={toast} />
        <ConfirmDialog />

        <div
          className="card flex flex-wrap gap-2 justify-content-center"
          style={{ marginTop: 'auto', marginBottom: '10rem' }}
        >
          <Divider></Divider>
          <div
            className="text-s font-normal text-900"
            style={{ fontFamily: 'Graphik Light' }}
          >
            <strong>{t('sidebar_component.total')}</strong>{' '}
            {formatCurrency(cartTotal)}
          </div>
          <Divider />
        </div>
        <div
          className="card flex flex-wrap gap-2 justify-content-center"
          style={{ marginTop: '1rem' }}
        >
          <button
            className={`meso-button-sidebar${cartItems.length === 0 || exceededProducts.length > 0 ? '-disabled' : ''}`}
            disabled={cartItems.length === 0 || exceededProducts.length > 0}
            onClick={() => {
              setVisibleModal(true);
            }}
          >
            {t('sidebar_component.purchase_order_btn')}
          </button>
          {visibleModal && (
            <Modal
              onAccept={accept}
              onCancel={() => setVisibleModal(false)}
              onClose={() => setVisibleModal(false)}
              items={
                <p>{`${cartItems.length} ${t('sidebar_component.confirm_dialog.items')}`}</p>
              }
            >
              <p>{t('sidebar_component.confirm_dialog.question')}</p>
            </Modal>
          )}
          <button
            className="meso-button-sidebar"
            onClick={() => {
              setVisibleSideBar(false);
            }}
          >
            {t('sidebar_component.hide_sidebar')}
          </button>
        </div>
      </Sidebar>
    </div>
  );
}
